<script lang="ts" setup>
import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
  faImage, faImages, faRepeat, faTrashCan,
} from '@fortawesome/pro-solid-svg-icons';
import {
  ref,
  onMounted,
  watch, nextTick,
} from 'vue';
import IoInputWrapper from '@/components/form/InputWrapper.vue';

library.add(faImage, faImages, faRepeat, faTrashCan);

const modelValue = defineModel<number | number[]>();
const props = withDefaults(defineProps<{
  label?: string
  types?: string | string[]
  multiple?: boolean
}>(), {
  label: undefined,
  types: () => ['application/pdf', 'image'],
});

const files = ref<any>([]);

if (!Array.isArray(modelValue.value)) {
  if (modelValue.value) {
    files.value = [modelValue.value];
  }
} else {
  files.value = modelValue.value;
}

const wp = ref<any>(null);
const wpMediaPostId = ref<any>(null);
const mediaSelect = ref<any>(null);
const fileLabel = ref<string>('');
const fileUrls = ref<string[]>([]);

const setFileLabels = (isInit = false) => {
  const mediaTitles: string[] = [];
  const mediaUrls: string[] = [];

  if (!isInit) {
    (files.value || []).forEach((mediaId: any) => {
      const attachment = wp.value.media.attachment(mediaId);
      mediaTitles.push((
        attachment.attributes.title
          || attachment.attributes.name
          || attachment.attributes.id
      ));

      const sizeKey = Object.keys(attachment?.attributes?.sizes || {})?.[0];
      mediaUrls.push(attachment?.attributes?.sizes?.[sizeKey]?.url);
    });

    fileLabel.value = mediaTitles.join(', ');
    fileUrls.value = mediaUrls;

    return;
  }

  const attachmentsRequests = (files.value || []).map(
    (mediaId: any) => wp.value.media.attachment(mediaId).fetch(),
  );

  Promise.all(attachmentsRequests).then((data: any) => {
    data.forEach((attachment: any) => {
      mediaTitles.push((
        attachment.title
          || attachment.name
          || attachment.id
      ));
      mediaUrls.push(attachment.sizes[Object.keys(attachment.sizes)[0]]?.url);
    });

    fileLabel.value = mediaTitles.join(', ');
    fileUrls.value = mediaUrls;
  }).catch(() => {
    fileLabel.value = (files.value || []).join(', ');
  });
};

function onMediaSelect() {
  const selection = mediaSelect.value.state().get('selection');
  files.value = [];
  selection.models.forEach((media: any) => {
    files.value.push(media.attributes.id);
  });
}

watch(() => files.value, (newFiles) => {
  modelValue.value = props.multiple ? newFiles : newFiles[0];
  setFileLabels();
}, {
  deep: true,
});

const onMediaSelectOpen = () => {
  if (files.value.length) {
    const selection = mediaSelect.value.state().get('selection');

    files.value.forEach((fileId: any) => {
      selection.add(wp.value.media.attachment(fileId));
    });
  }
};

const initMedia = () => {
  // eslint-disable-next-line no-multi-assign
  mediaSelect.value = wp.value.media.frames.file_frame = wp.value.media({
    title: 'Dokument wählen',
    frame: 'select',
    library: {
      type: props.types,
    },
    multiple: props.multiple,
  });

  mediaSelect.value.on('select', onMediaSelect);
  mediaSelect.value.on('open', onMediaSelectOpen);
};

const checkForWpMediaObject = () => {
  if (wp.value === null && window.wp !== undefined && window.wp.media !== undefined) {
    wp.value = window.wp;
    wpMediaPostId.value = wp.value.media.model.settings.post.id;
  }
};

onMounted(() => {
  window.addEventListener('load', checkForWpMediaObject);
  checkForWpMediaObject();
  setFileLabels(true);
});

const openMediaSelect = () => {
  checkForWpMediaObject();

  if (!mediaSelect.value) {
    wp.value.media.model.settings.post.id = wpMediaPostId.value;

    initMedia();
  }

  mediaSelect.value.open();
};

const removeItem = (inx: number) => {
  const removedFileId = files.value.splice(inx, 1);
  const selection = mediaSelect.value?.state().get('selection');

  if (!selection) return;

  selection.remove(removedFileId);

  nextTick(setFileLabels);
};
</script>

<template>
  <IoInputWrapper :label="label">
    <div
      class="media-field__image-cont"
      :style="{
        gridTemplateColumns: `repeat(${fileUrls.length + 1}, 150px)`,
      }"
    >
      <div
        v-for="(url, inx) in fileUrls"
        :key="url"
        class="image"
        :style="{
          backgroundImage: `url(${url})`,
        }"
      >
        <div
          class="change"
          @click="openMediaSelect"
        >
          <FontAwesomeIcon icon="fa-solid fa-repeat" />
        </div>
        <div
          class="trash"
          @click="removeItem(inx)"
        >
          <FontAwesomeIcon icon="fa-solid fa-trash-can" />
        </div>
      </div>
      <div
        v-if="multiple || fileUrls.length === 0"
        class="add-image"
        @click="openMediaSelect"
      >
        <div>
          <FontAwesomeIcon :icon="`fa-solid fa-${multiple ? 'images' : 'image'}`" />
        </div>
      </div>
    </div>
    <input
      type="hidden"
      :value="fileLabel"
      style="width: 100%;"
      readonly
    >
  </IoInputWrapper>
</template>

<style lang="scss">
.media-field__image-cont {
  display: grid;
  height: 150px;
  width: 100%;
  gap: 30px;

  > div {
    background-repeat: no-repeat;
    background-size: cover;
    border-radius: 40px;
    box-shadow: 0 3px 6px rgba(0,0,0,.15);

    &.image {
      display: grid;
      gap: 20px;
      grid-template-columns: repeat(2, auto);
      grid-template-rows: 1fr;
      align-items: end;
      justify-content: space-between;
      padding: 15px;
      font-size: 14px;

      .trash {
        color: red;
        padding: 10px;
        background-color: rgba(255, 255, 255, 0.5);
        box-shadow: 0 1px 2px rgba(0,0,0,.15);
        border-radius: 20px;
        line-height: 1;
        cursor: pointer;
        transition: background-color .375s ease-out;

        &:hover {
          background-color: rgba(255, 255, 255, 1);
        }
      }

      .change {
        color: #38383A;
        background-color: #ffffff;
        background-color: rgba(255, 255, 255, 0.5);
        box-shadow: 0 1px 2px rgba(0,0,0,.15);
        border-radius: 20px;
        padding: 10px;
        line-height: 1;
        cursor: pointer;
        transition: background-color .375s ease-out;

        &:hover {
          background-color: rgba(255, 255, 255, 1);
        }
      }
    }

    &.add-image {
      background-color: #fff;
      display: grid;
      grid-template-columns: auto;
      justify-content: center;
      align-items: center;
      font-size: 24px;
      cursor: pointer;
    }
  }
}
</style>
